import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, Descriptions, Tag, Image, Spinner, EmptyState, Typography, Table, Space, Button, Carousel } from '../../ui';
import { PlayCircle, FileText } from 'lucide-react';
import { request } from '../../api/client';
import { toImageUrls } from '../../lib/imageUrls';
import { fmtUom } from '../../lib/format';
import { boxPackingLabel } from '../../lib/packaging';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const num = (v: any) => Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

// Catalogue languages (matches the upload form / backend CATALOGUE_LANGS).
const CAT_LANGS: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी · Hindi' },
  { code: 'mr', label: 'मराठी · Marathi' },
  { code: 'or', label: 'ଓଡ଼ିଆ · Odia' },
  { code: 'bn', label: 'বাংলা · Bengali' },
  { code: 'gu', label: 'ગુજરાતી · Gujarati' },
];

/**
 * Read-only product detail modal opened from the products table. The list only
 * carries catalog+stock fields, so it fetches the full product record for the
 * shared info + media (video / brochure) + gallery. For a master (group) row it
 * also aggregates every variant's image and lists the variants.
 */
export function ProductDetailModal({ product, onClose }: { product: any; onClose: () => void }) {
  const isGroup = !!product?._isGroup;
  const fetchId = isGroup ? product?.id : product?.productId;
  const numericId = fetchId != null && /^\d+$/.test(String(fetchId));

  const { data: p, isLoading } = useQuery({
    queryKey: ['product-detail', fetchId],
    queryFn: () => request<any>({ url: `/products/${fetchId}` }),
    enabled: !!product && numericId,
  });

  // Per-language catalogue PDFs (shared by all variants of the master/group).
  const { data: catalogues } = useQuery({
    queryKey: ['product-catalogues', fetchId],
    queryFn: () => request<Record<string, string>>({ url: `/products/${fetchId}/catalogues` }),
    enabled: !!product && numericId,
  });
  const catLangs = CAT_LANGS.filter((l) => catalogues?.[l.code]);

  const variants: any[] = isGroup ? (product?.children ?? []) : [];
  // All images: the fetched product's gallery + each variant's cover (deduped).
  const images = useMemo(() => {
    const set = new Set<string>();
    const gallery = toImageUrls(p?.imageUrls);
    if (gallery.length) gallery.forEach((u) => u && set.add(u));
    else if (p?.imageUrl) set.add(p.imageUrl);
    variants.forEach((v) => v?.imageUrl && set.add(v.imageUrl));
    return Array.from(set);
  }, [p, variants]);

  const title = p ? (isGroup ? p.name : `${p.name}${p.variantLabel ? ` · ${p.variantLabel}` : ''}`) : 'Product details';

  return (
    <Modal open={!!product} onOpenChange={(o) => { if (!o) onClose(); }} width={920} title={title}>
      {isLoading || !p ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spinner /></div>
      ) : (
        <>
          {/* Top: two columns — gallery+media on the left, key facts on the right. */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ flex: '0 0 360px', maxWidth: '100%' }}>
              {/* Image gallery — auto-sliding carousel for multiple, single previewable image otherwise. */}
              {images.length ? (
                <Image.PreviewGroup>
                  {images.length === 1 ? (
                    <div style={{ textAlign: 'center', background: '#fafafa', borderRadius: 8, padding: 6 }}>
                      <Image src={images[0]} style={{ maxHeight: 300, objectFit: 'contain' }} />
                    </div>
                  ) : (
                    <Carousel autoplay autoplaySpeed={2500} arrows draggable dots style={{ background: '#fafafa', borderRadius: 8 }}>
                      {images.map((src, i) => (
                        <div key={i}>
                          <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
                            <Image src={src} style={{ maxHeight: 200, objectFit: 'contain' }} />
                          </div>
                        </div>
                      ))}
                    </Carousel>
                  )}
                </Image.PreviewGroup>
              ) : (
                <EmptyState title="No image" />
              )}

              {/* Media — videos + brochure */}
              {(() => {
                const videos = toImageUrls(p.videoUrls).length ? toImageUrls(p.videoUrls) : (p.videoUrl ? [p.videoUrl] : []);
                return (videos.length || p.brochureUrl) ? (
                  <Space style={{ marginTop: 12 }} wrap>
                    {videos.map((u, i) => (
                      <Button key={i} icon={<PlayCircle size={16} />} href={u} target="_blank" rel="noreferrer">
                        {videos.length > 1 ? `Video ${i + 1}` : 'Watch video'}
                      </Button>
                    ))}
                    {p.brochureUrl && <Button icon={<FileText size={16} />} href={p.brochureUrl} target="_blank" rel="noreferrer">View brochure</Button>}
                  </Space>
                ) : null;
              })()}
            </div>

            {/* Right: key facts in two columns of label/value. */}
            <div style={{ flex: '1 1 360px', minWidth: 300 }}>
              <Descriptions bordered size="small" column={2}>
                {!isGroup && <Descriptions.Item label="SKU">{p.sku}</Descriptions.Item>}
                {!isGroup && <Descriptions.Item label="Variant">{p.variantLabel ?? '—'}</Descriptions.Item>}
                {!isGroup && <Descriptions.Item label="MRP">{inr(p.mrp)}</Descriptions.Item>}
                {!isGroup && <Descriptions.Item label="Dealer price">{inr(product?.dealerPrice ?? p.price)}</Descriptions.Item>}
                {!isGroup && <Descriptions.Item label="MOQ">{num(p.moq)} {p.uomCode}</Descriptions.Item>}
                {!isGroup && <Descriptions.Item label="Reorder level">{num(p.reorderLevel)} {p.uomCode}</Descriptions.Item>}
                {!isGroup && <Descriptions.Item label="Pack size">{fmtUom(p.packSize, p.uomCode)}</Descriptions.Item>}
                {!isGroup && (
                  <Descriptions.Item label="Box packing">
                    {boxPackingLabel({ boxPackingEnabled: product?.boxPackingEnabled ?? p.boxPackingEnabled, unitsPerBox: product?.unitsPerBox ?? p.unitsPerBox })}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="HSN">{p.hsnCode ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="UoM">{p.uomCode}</Descriptions.Item>
                <Descriptions.Item label="Shelf life">{Number(p.shelfLifeMonths) === 0 ? 'No expiry' : `${p.shelfLifeMonths} months`}</Descriptions.Item>
                <Descriptions.Item label="Group">{p.groupCode ?? '—'}</Descriptions.Item>
                {!isGroup && <Descriptions.Item label="In stock">{num(product?.stock?.total)} {p.uomCode}</Descriptions.Item>}
                <Descriptions.Item label="Status">
                  <Tag tone={p.isActive ? 'green' : 'neutral'}>{p.isActive ? 'Active' : 'Inactive'}</Tag>
                </Descriptions.Item>
              </Descriptions>
            </div>
          </div>

          {/* Description — full width below the two columns. */}
          {p.description && (
            <Descriptions bordered size="small" column={1} style={{ marginTop: 16 }}>
              <Descriptions.Item label="Description">
                <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{p.description}</Typography.Paragraph>
              </Descriptions.Item>
            </Descriptions>
          )}

          {/* Catalogue PDFs by language (shared across the product's variants). */}
          {catLangs.length > 0 && (
            <>
              <Typography.Text strong style={{ display: 'block', margin: '16px 0 8px' }}>
                Catalogue
              </Typography.Text>
              <Space wrap>
                {catLangs.map((l) => (
                  <Button key={l.code} icon={<FileText size={16} />} href={catalogues![l.code]} target="_blank" rel="noreferrer">
                    {l.label}
                  </Button>
                ))}
              </Space>
            </>
          )}

          {/* Variants (master row only) */}
          {isGroup && variants.length > 0 && (
            <>
              <Typography.Text strong style={{ display: 'block', margin: '14px 0 6px' }}>
                Variants ({variants.length})
              </Typography.Text>
              <Table
                size="small" pagination={false} rowKey="productId" dataSource={variants}
                columns={[
                  { title: 'SKU', dataIndex: 'sku' },
                  { title: 'Variant', dataIndex: 'variantLabel', render: (v: any) => v ?? '—' },
                  { title: 'MRP', dataIndex: 'mrp', align: 'right' as const, render: (v: any) => inr(v) },
                  { title: 'Price', dataIndex: 'price', align: 'right' as const, render: (v: any) => inr(v) },
                  { title: 'Pack', dataIndex: 'packSize', align: 'right' as const, render: (v: any) => fmtUom(v, p.uomCode) },
                  { title: 'Box', dataIndex: 'unitsPerBox', align: 'right' as const, render: (v: any, r: any) => (r.boxPackingEnabled && v ? `${num(v)}/box` : '—') },
                  { title: 'Stock', align: 'right' as const, render: (_: any, v: any) => num(v.stock?.total) },
                ]}
              />
            </>
          )}
        </>
      )}
    </Modal>
  );
}
